import React, { useCallback, useState } from 'react';
import { MessageType } from '@graphql/generated-base';
import { createContext } from '@shared/utils/createContext';
import { useInternalMessageSearchLazyQuery } from '../api/internalMessageSearch.graphql.generated';
import { useResendSelection } from '../components/ResendSelection/useResendSelection';
import type { MessagesV2SearchValues, MessagesV2StateContext } from '../MessagesV2.types';

const [MessagesV2ContextProvider, useMessagesV2State] = createContext<MessagesV2StateContext>({ name: 'MessagesV2State' });

export const MessagesV2StateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [lastValues, setLastValues] = useState<MessagesV2SearchValues | null>(null);

  const [executeSearch, { data, loading, error, called, refetch: refetchQuery }] = useInternalMessageSearchLazyQuery();

  /**
   * Selection lives here rather than in the route so that every path which replaces the result set
   * clears it in the same place.
   *
   * Selection is a plain set of message ids, held independently of the query, so ids survive across
   * result sets — and it is only ever intersected with the rows on screen. Left behind, a tick
   * either silently drops out of a resend or, when the same id comes back under different criteria,
   * reappears already ticked against a selection the operator never made. `refetch` deliberately
   * does not clear: it re-runs the same page, so the ticks still refer to the rows they were made on.
   */
  const selection = useResendSelection();
  const { clearSelection } = selection;

  const buildCriteria = useCallback((values: MessagesV2SearchValues) => {
    const criteria: Record<string, unknown> = {};
    if (values.email) criteria.email = values.email;
    if (values.fromEmail) criteria.fromEmail = values.fromEmail;
    if (values.replyTo) criteria.replyTo = values.replyTo;
    if (values.phoneE164) criteria.phone = values.phoneE164;
    if (values.eventId) criteria.eventId = values.eventId;
    if (values.personId) criteria.personId = values.personId;
    if (values.guestKey) criteria.guestKey = values.guestKey;
    if (values.messageBatchId) criteria.messageBatchId = values.messageBatchId;
    if (values.stationeryOrderId) criteria.stationeryOrderId = values.stationeryOrderId;
    if (values.channel !== 'all') criteria.type = values.channel === 'email' ? MessageType.email : MessageType.sms;
    if (values.messageType) criteria.messageType = values.messageType;
    if (values.startCreateDate) criteria.startCreateDate = values.startCreateDate;
    if (values.endCreateDate) criteria.endCreateDate = values.endCreateDate;
    return criteria;
  }, []);

  const runSearch = useCallback(
    (values: MessagesV2SearchValues) => {
      clearSelection();
      setLastValues(values);
      setPage(1);
      setPageSize(values.pageSize);
      executeSearch({
        variables: {
          criteria: buildCriteria(values),
          limit: values.pageSize,
          offset: 0,
        },
      });
    },
    [executeSearch, buildCriteria, clearSelection],
  );

  const changePage = useCallback(
    (p: number) => {
      if (!lastValues) return;
      clearSelection();
      setPage(p);
      executeSearch({
        variables: {
          criteria: buildCriteria(lastValues),
          limit: lastValues.pageSize,
          offset: (p - 1) * lastValues.pageSize,
        },
      });
    },
    [executeSearch, buildCriteria, lastValues, clearSelection],
  );

  /**
   * Re-runs the current page with the same criteria. Used after a bulk resend so the new resend
   * rows appear without the operator reloading or re-searching. Paging state is preserved
   * deliberately — a resend shouldn't bounce them back to page 1.
   */
  const refetch = useCallback(() => {
    // Apollo's own refetch re-runs the last executed query against the network, which is what we
    // need — the whole point is to see rows that did not exist when the page was first fetched.
    // Undefined until a search has run, so a resend before any search is a no-op rather than a
    // crash. The rejection is swallowed rather than left unhandled: `refetch()` rejects on a
    // network or GraphQL error, and that error already reaches the operator through this hook's
    // `error`.
    refetchQuery?.().catch(() => undefined);
  }, [refetchQuery]);

  const changePageSize = useCallback(
    (s: number) => {
      if (!lastValues) return;
      clearSelection();
      setPage(1);
      setPageSize(s);
      const updatedValues = { ...lastValues, pageSize: s };
      setLastValues(updatedValues);
      executeSearch({
        variables: {
          criteria: buildCriteria(updatedValues),
          limit: s,
          offset: 0,
        },
      });
    },
    [executeSearch, buildCriteria, lastValues, clearSelection],
  );

  const messages = data?.internalMessageSearchResults.allResults.messages ?? [];
  const totalSearchCount = data?.internalMessageSearchResults.allResults.totalSearchCount ?? 0;

  return (
    <MessagesV2ContextProvider
      value={{
        messages,
        totalSearchCount,
        loading,
        error,
        called,
        page,
        pageSize,
        runSearch,
        changePage,
        changePageSize,
        refetch,
        ...selection,
      }}
    >
      {children}
    </MessagesV2ContextProvider>
  );
};

export { useMessagesV2State };
